import {
  createSlice,
  createAsyncThunk,
  createSelector,
} from '@reduxjs/toolkit';
import {ApolloClient, NormalizedCacheObject} from '@apollo/client';
import {RootState} from '@src/redux/store';
import {BASE_URL} from '@src/helpers/constants';
import {retrieveItem} from '@src/helpers/functions';
import * as TYPES from '@src/redux/types';

const GRAPHQL_QUERY = `
  mutation exportReport($filter: reportFilterInput, $data: reportExportInput) {
    exportReport(filter: $filter, data: $data) {
      id
      isQueued
      fileName
      mimeType
      base64
      rowCount
      isTruncated
      maxRowCount
      exclusion {
        isApplied
        excludedQuestionCount
        excludedSubjectCount
        noFindingAnswerList
      }
      error {
        message
        statusCode
      }
    }
  }
`;

export type ExportReportParams = {
  filter?: TYPES.ReportFilterInput;
  data?: TYPES.ReportExportInput;
};

const initialState: TYPES.ReportExportState = {
  result: null,
  error: null,
  loading: false,
};

export const exportReportRequest = createAsyncThunk<
  TYPES.ReportExportResponse,
  ExportReportParams,
  {extra: ApolloClient<NormalizedCacheObject>}
>('report/export', async (params: ExportReportParams) => {
  const sessionId = retrieveItem('sessionId') ?? '';
  try {
    const response = await fetch(BASE_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        sessionId: sessionId,
      },
      body: JSON.stringify({
        operationName: 'exportReport',
        query: GRAPHQL_QUERY,
        variables: params ?? {},
      }),
    });
    const json = await response.json();
    if (json?.errors?.length) {
      const first = json.errors[0];
      return Promise.reject({
        message: first.message || 'Export failed',
        statusCode: String(response.status),
      });
    }
    return json?.data?.exportReport as TYPES.ReportExportResponse;
  } catch (error) {
    const err = error as {message?: string; statusCode?: string};
    return Promise.reject({
      message: err.message || 'An error occurred',
      statusCode: err.statusCode || '500',
    });
  }
});

const exportReportSlice = createSlice({
  name: 'export_report',
  initialState: initialState,
  reducers: {
    exportReportClear: state => {
      state.result = null;
      state.error = null;
      state.loading = false;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(exportReportRequest.pending, state => {
        state.result = null;
        state.error = null;
        state.loading = true;
      })
      .addCase(exportReportRequest.fulfilled, (state, action) => {
        state.result = action.payload;
        state.loading = false;
      })
      .addCase(exportReportRequest.rejected, (state, action) => {
        state.error =
          (action.payload as TYPES.Error) ||
          {message: 'Failed to export', statusCode: '500'};
        state.loading = false;
      });
  },
});

export const {exportReportClear} = exportReportSlice.actions;

export const exportReport = createSelector(
  (state: RootState) => state.report.exportReport.result,
  (state: RootState) => state.report.exportReport.error,
  (state: RootState) => state.report.exportReport.loading,
  (result, error, loading) => ({result, error, loading}),
);

export default exportReportSlice.reducer;
